import moment from 'moment';
import styled from 'styled-components';

import { useMutation, useQuery } from '@apollo/client';
import type React from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useIntl } from 'react-intl';
import { useAppState, useDispatch } from 'src/state';
import { useGymStructure } from 'app/providers/gym-structure';
import { useLessonCloneAccess } from 'app/hooks/use-partner-permission';
import { getGreetingPrefix } from 'app/utils/greeting';

import BannerRail from 'app/ui/components/molecules/banner-rail';
import type { BannerCardData } from 'app/ui/components/atoms/dynamic-banner';
import ClassCard, { ClassCardContainer } from 'app/ui/components/atoms/class-card';
import Typography from 'app/ui/components/atoms/typography';
import GenericPage from 'app/ui/components/layouts/generic-page';
import ErrorComponent from 'app/ui/components/molecules/error';
import HorizontalRail from 'app/ui/components/molecules/rail';
import NavigationCard from 'app/ui/components/molecules/navigation-card';
import CardContainer from 'src/app/ui/components/molecules/navigation-card/container';
import WOTD from 'app/ui/components/molecules/wotd';

import { type LessonToWorkoutDetails, lessonToWorkout } from 'app/utils/workouts';

import useConfig from 'app/config';
import { HomePage as HOME } from 'app/pages/home/home.gql';
import { routes } from 'app/routes/constants';
import { setLanguage } from 'src/actions/language';
import { setUserName } from 'src/actions/user';

import type {
  HomePageQuery,
  HomePageQueryVariables,
  Language,
  Lesson,
  ProgressivePlan,
  ProgressivePlanInstance,
  ResourceList,
  SaveLessonByIdMutation,
  SaveLessonByIdMutationVariables,
  UnsaveLessonByIdMutation,
  UnsaveLessonByIdMutationVariables,
  UpdatePreferredLanguageMutation,
  UpdatePreferredLanguageMutationVariables,
} from 'app/types/graphql';

import { UpdatePreferredLanguage as UPDATE_LANGUAGE } from 'app/ui/components/molecules/picker/language/update-language.gql';
import {
  SaveLessonByIdClassCard as SAVE_LESSON,
  UnsaveLessonByIdClassCard as UNSAVE_LESSON,
} from 'app/ui/components/atoms/class-card/mutations.gql';
import { lengthFromDurationSeconds } from 'app/utils/duration-handler';
import { amplitude } from 'src/app/utils/analytics/amplitude/client';
import * as amplitudeLib from '@amplitude/analytics-browser';
import { UserProperties } from 'src/app/utils/analytics/types';
import { generateAnonymousUsername } from 'src/app/utils/anonymous-username-generator';
import ResourceListGrid from 'src/app/ui/components/molecules/resource-list-card/grid';

import { trackEvent, setGlobalAnalyticsProperties } from 'src/app/utils/analytics/tracker';
import { appendSessionId } from 'app/utils/analytics/recordrelay/session';
import LibraryTrainingPlans from 'src/app/ui/components/molecules/library-training-plans';
import Loading from 'src/app/ui/components/molecules/loading';

export type WOTDDetails = {
  lessonId: string;
  mainDetails: string;
  intensity: string;
  srcSet: string;
  cardTitle: string;
  duration: string;
};

const NavigationCardContainer = styled(CardContainer)`
  padding-bottom: ${({ theme }) => theme.spacing(16)};
`;

const PlaybookTitle = styled(Typography)`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const GreetingTitle = styled(Typography)`
  margin-bottom: ${({ theme }) => theme.spacing(7)};
`;

const lessonToWotd = (lesson: Lesson): WOTDDetails => ({
  lessonId: lesson.id,
  mainDetails: lesson.description,
  intensity: typeof lesson.rpe === 'number' ? `RPE ${lesson.rpe}` : '',
  srcSet: lesson.image?.url || lesson.image?.signedUrl,
  cardTitle: lesson.name,
  duration: lengthFromDurationSeconds(lesson.duration),
});

type HomePageProps = {
  wotd: WOTDDetails | null;
  workouts: LessonToWorkoutDetails[];
  isDataLoading: boolean;
  banners: BannerCardData[];
  favouriteLessons: LessonToWorkoutDetails[];
  resourceList: ResourceList;
  planInstances: ProgressivePlanInstance[];
  plans: Pick<ProgressivePlan, 'id'>[];
  onSaveLesson: (lessonId: string) => Promise<{ isSaved: boolean }>;
  onUnsaveLesson: (lessonId: string) => Promise<{ isSaved: boolean }>;
};

export const HomePage: React.FC<HomePageProps> = ({
  wotd,
  workouts,
  isDataLoading,
  banners,
  favouriteLessons,
  resourceList,
  planInstances,
  plans,
  onSaveLesson,
  onUnsaveLesson,
}) => {
  const intl = useIntl();
  const { config } = useConfig();
  const navigate = useNavigate();
  const userName = useAppState(({ user }) => user.userName);

  const canAccessPersonalisedHomescreen = config.REACT_APP_PERSONALISED_HOMESCREEN_ENABLED;
  const { canCloneWorkout: canAccessWorkoutBuilder } = useLessonCloneAccess();
  const canAccessMultiscreen = true;
  const progressivePlansEnabled = config.REACT_APP_PROGRESSIVE_PLANS_ENABLED;
  const workoutsHomepageEnabled = config.REACT_APP_WORKOUTS_HOMEPAGE_ENABLED;

  // Slightly magic, but this is the start of week 0
  const startDate = moment.utc('2024-06-10').startOf('day');
  const now = moment.utc();

  const weekNumber = Math.max(now.diff(startDate, 'week'), 1);

  const currentWeekStartDate = startDate.clone().add(weekNumber, 'weeks').startOf('week').add(1, 'days');
  const currentWeekEndDate = currentWeekStartDate.clone().add(6, 'days');

  const formattedStartDate = currentWeekStartDate.format('DD/MM');
  const formattedEndDate = currentWeekEndDate.format('DD/MM');

  const wodWeekText = intl.formatMessage({ id: 'home.copy.week' });

  const libraryTitle = intl.formatMessage({ id: 'home.homeCards.library.title' });
  const libraryContent = intl.formatMessage({ id: 'home.homeCards.library.content' });
  const playbookTitle = intl.formatMessage({ id: 'home.homeCards.playbook.title' });
  const playbookContent = intl.formatMessage({ id: 'home.homeCards.playbook.content' });
  const supportTitle = intl.formatMessage({ id: 'home.homeCards.support.title' });
  const supportContent = intl.formatMessage({ id: 'home.homeCards.support.content' });
  const multiscreenTitle = intl.formatMessage({ id: 'home.homeCards.multiscreen.title' });
  const multiscreenContent = intl.formatMessage({ id: 'home.homeCards.multiscreen.content' });
  const workoutBuilderTitle = intl.formatMessage({ id: 'home.homeCards.workoutBuilder.title' });
  const workoutBuilderContent = intl.formatMessage({ id: 'home.homeCards.workoutBuilder.content' });

  const handleViewAllWeeksButtonClick = () => {
    navigate(routes.WEEKLY_WORKOUTS);
  };

  const greetingPrefix = getGreetingPrefix();
  const greetingMessage = userName
    ? `${intl.formatMessage({ id: `home.greetings.${greetingPrefix}` })}, ${userName}!`
    : `${intl.formatMessage({ id: `home.greetings.${greetingPrefix}` })}`;

  const bannerOnClick = (banner: BannerCardData, index: number) => () => {
    trackEvent('banner_clicked', {
      banner_id: banner.id,
      banner_title: banner.title,
      banner_description: banner.description,
      banner_image_url: banner.image.signedUrl,
      banner_mobile_image_url: banner.mobileImage?.signedUrl,
      banner_link_url: banner.link || undefined,
      banner_position: index,
    });
    if (banner.link) {
      window.open(banner.link);
    }
  };

  return (
    <GenericPage pageTitle="Home">
      {greetingMessage && <GreetingTitle variant="h4">{greetingMessage}</GreetingTitle>}
      {canAccessPersonalisedHomescreen && banners.length > 0 && (
        <BannerRail banners={banners} onBannerClick={bannerOnClick} />
      )}
      {canAccessPersonalisedHomescreen && (
        <NavigationCardContainer>
          <NavigationCard
            title={libraryTitle}
            content={libraryContent}
            image="/library.jpeg"
            url={config.REACT_APP_NEW_LIBRARY_ENABLED ? '/library' : '/browse'}
            isNew={true}
          />
          <NavigationCard title={playbookTitle} content={playbookContent} image="/playbook.jpeg" url="/resources" />
          {canAccessMultiscreen ? (
            <NavigationCard
              title={multiscreenTitle}
              content={multiscreenContent}
              image="/multiscreen.jpeg"
              url={appendSessionId(
                `${config.REACT_APP_WHITEBOARD_URL}?subpath=multiscreen/setup&client_id=${config.REACT_APP_CLIENT_ID_FOR_ONE_FIIT}`,
              )}
              isNew={true}
            />
          ) : (
            <NavigationCard title={supportTitle} content={supportContent} image="/support.png" url="/faq" />
          )}
          <NavigationCard
            title={workoutBuilderTitle}
            content={workoutBuilderContent}
            image="/workout-builder.png"
            isActive={canAccessWorkoutBuilder}
            url={
              canAccessWorkoutBuilder
                ? appendSessionId(
                    `${config.REACT_APP_WORKOUT_BUILDER_URL}?client_id=${config.REACT_APP_CLIENT_ID_FOR_ONE_FIIT}`,
                  )
                : undefined
            }
            isNew={true}
          />
        </NavigationCardContainer>
      )}

      {progressivePlansEnabled && (
        <LibraryTrainingPlans titleVariant="h4" planInstances={planInstances} plans={plans} />
      )}

      {workoutsHomepageEnabled && (
        <>
          {!canAccessPersonalisedHomescreen && wotd && <WOTD {...wotd} isLoading={isDataLoading} />}
          <HorizontalRail
            title={
              <Typography variant="h4">{`${wodWeekText} ${weekNumber} - ${formattedStartDate}-${formattedEndDate}`}</Typography>
            }
            handleViewAllButtonClick={handleViewAllWeeksButtonClick}
          >
            {workouts.map((workout, i) => (
              <ClassCardContainer key={`workout-${workout.id}-${i}`}>
                <ClassCard
                  lessonId={workout.id}
                  srcSet={workout.srcSet}
                  title={workout.title}
                  to={routes.WORKOUT.replace(':id', workout.id)}
                  intensity={workout.intensity}
                  equipment={workout.equipment}
                  isSaved={workout.isSaved}
                  isCompleted={workout.isCompleted}
                  onSaveLesson={onSaveLesson}
                  onUnsaveLesson={onUnsaveLesson}
                />
              </ClassCardContainer>
            ))}
          </HorizontalRail>
        </>
      )}
      {canAccessPersonalisedHomescreen && favouriteLessons.length > 0 && (
        <HorizontalRail title={<Typography variant="h4">Favourites</Typography>}>
          {favouriteLessons.map((lesson) => (
            <ClassCardContainer key={`lesson-${lesson.id}-fav`}>
              <ClassCard
                lessonId={lesson.id}
                srcSet={lesson.srcSet}
                title={lesson.title}
                to={routes.WORKOUT.replace(':id', lesson.id)}
                intensity={lesson.intensity}
                equipment={lesson.equipment}
                isSaved={lesson.isSaved}
                isCompleted={lesson.isCompleted}
                onSaveLesson={onSaveLesson}
                onUnsaveLesson={onUnsaveLesson}
              />
            </ClassCardContainer>
          ))}
        </HorizontalRail>
      )}
      {canAccessPersonalisedHomescreen && resourceList && (
        <>
          <PlaybookTitle variant="h4">Playbook Quick Links</PlaybookTitle>
          <ResourceListGrid resourceLists={resourceList.resourceLists || []} />
        </>
      )}
    </GenericPage>
  );
};

export const Home: React.FC = () => {
  const dispatch = useDispatch();
  const { config } = useConfig();
  const { setGymStructure, setLoading } = useGymStructure();

  const userId = useAppState(({ auth }) => auth.userId) as number;
  const selectedLanguage = useAppState(({ language }) => language.preferredLanguage);
  const currentUserName = useAppState(({ user }) => user.userName);

  const date = moment.utc().startOf('day').toISOString();

  const monday = moment.utc().startOf('isoWeek').startOf('day').toISOString();
  const sunday = moment.utc().endOf('isoWeek').endOf('day').toISOString();

  const [updatePreferredLanguage] = useMutation<
    UpdatePreferredLanguageMutation,
    UpdatePreferredLanguageMutationVariables
  >(UPDATE_LANGUAGE);

  const [saveLesson] = useMutation<SaveLessonByIdMutation, SaveLessonByIdMutationVariables>(SAVE_LESSON);
  const [unsaveLesson] = useMutation<UnsaveLessonByIdMutation, UnsaveLessonByIdMutationVariables>(UNSAVE_LESSON);

  const handleSaveLesson = async (lessonId: string): Promise<{ isSaved: boolean }> => {
    if (!userId) {
      throw new Error('User ID is required');
    }
    const result = await saveLesson({
      variables: { id: lessonId, input: { user: { id: userId } } },
    });
    return { isSaved: result.data?.saveLessonById.isSaved ?? false };
  };

  const handleUnsaveLesson = async (lessonId: string): Promise<{ isSaved: boolean }> => {
    if (!userId) {
      throw new Error('User ID is required');
    }
    const result = await unsaveLesson({
      variables: { id: lessonId, input: { user: { id: userId } } },
    });
    return { isSaved: result.data?.unsaveLessonById.isSaved ?? false };
  };

  const queryVariables: HomePageQueryVariables = {
    date,
    dateRange: { from: monday, to: sunday },
    userId,
    resourceListId: config.REACT_APP_ROOT_RESOURCE_LIST_ID,
  };

  const { data, loading, error } = useQuery<HomePageQuery, HomePageQueryVariables>(HOME, {
    variables: queryVariables,
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    const updateLanguage = async () => {
      try {
        await updatePreferredLanguage({ variables: { userId, code: selectedLanguage?.code || 'en' } });
      } catch (error) {
        console.error('Error updating preferred language:', error);
      }
    };
    if (selectedLanguage) {
      updateLanguage();
      // Only set user properties if amplitude is available (user has consented)
      if (amplitude && typeof amplitude.identify === 'function') {
        const identifyEvent = new amplitudeLib.Identify();
        identifyEvent.set(UserProperties.PREFERRED_LANGUAGE, selectedLanguage.code);
        amplitude.identify(identifyEvent);
      }
    }
  }, []);
  useEffect(() => {
    if (!selectedLanguage && !loading) {
      const preferredLanguage = data?.user?.preferredLanguage as Language;
      dispatch(setLanguage(preferredLanguage));
    }
  }, [data, loading, selectedLanguage]);

  useEffect(() => {
    if (data?.user?.email && userId) {
      const anonUsername = generateAnonymousUsername(data.user.email);
      if (amplitude && typeof amplitude.setUserId === 'function') {
        amplitude.setUserId(anonUsername);
      }
    }
  }, [data, userId]);

  useEffect(() => {
    if (data?.user && !loading) {
      if (data.user.gym.country) {
        setGlobalAnalyticsProperties({ country_code: data.user.gym.country });
      }

      if (amplitude && typeof amplitude.identify === 'function') {
        const identifyEvent = new amplitudeLib.Identify();
        identifyEvent.set(UserProperties.HYROX_API_USER_ID, String(data.user.id));
        identifyEvent.set(UserProperties.GYM_ID, String(data.user.gym.id));
        identifyEvent.set(UserProperties.GYM_NAME, data.user.gym.name);
        if (data.user.gym.country) {
          identifyEvent.set(UserProperties.GYM_COUNTRY, data.user.gym.country);
        }
        if (data.user.gym.type?.name) {
          identifyEvent.set(UserProperties.GYM_TYPE_NAME, data.user.gym.type.name);
        }
        if (data.user.gym.structure) {
          identifyEvent.set(UserProperties.GYM_STRUCTURE, data.user.gym.structure);
        }
        amplitude.identify(identifyEvent);
      }
    }
  }, [data, loading]);

  useEffect(() => {
    if (!loading) {
      setGymStructure(data?.user?.gym?.structure || undefined);
      setLoading(false);
    }
  }, [data, loading, setGymStructure, setLoading]);

  useEffect(() => {
    if (data?.user?.firstName && !loading) {
      const { firstName } = data.user;
      const trimmedFirstName = firstName?.trim() ?? null;
      // Only update if the name has changed
      if (trimmedFirstName !== currentUserName) {
        dispatch(setUserName(trimmedFirstName));
      }
    }
  }, [data, loading, dispatch, currentUserName]);

  // cache-and-network keeps `loading` true while it refetches, so only show the
  // full-page loader on the initial load when there is no cached data to render.
  if (loading && !data) {
    return <Loading />;
  }

  if (error) {
    console.error('Error fetching home page data:', error);
    // A background-refresh error still leaves cached data usable; only fall back
    // to the error page when there is nothing cached to show.
    if (!data) {
      return <ErrorComponent />;
    }
  }

  const wotd = data?.wotd?.[0]?.lesson ? lessonToWotd(data.wotd[0]?.lesson as Lesson) : null;
  const workouts =
    (data?.weeklyWorkouts
      ?.map(({ lesson }) => lesson && lessonToWorkout(lesson))
      ?.filter((lesson) => !!lesson) as LessonToWorkoutDetails[]) || [];

  const banners = data?.user?.banners || [];

  const favouriteLessons =
    (data?.user?.savedLessons
      ?.map((lesson) => lesson && lessonToWorkout(lesson))
      ?.filter((lesson) => !!lesson) as LessonToWorkoutDetails[]) || [];

  const planInstances = (data?.progressivePlanInstances?.planInstances ?? []) as ProgressivePlanInstance[];
  const plans = (data?.progressivePlans?.nodes ?? []) as Pick<ProgressivePlan, 'id'>[];

  return (
    <>
      <HomePage
        wotd={wotd}
        workouts={workouts}
        isDataLoading={loading}
        banners={banners}
        favouriteLessons={favouriteLessons}
        resourceList={data?.resourceList as ResourceList}
        planInstances={planInstances}
        plans={plans}
        onSaveLesson={handleSaveLesson}
        onUnsaveLesson={handleUnsaveLesson}
      />
    </>
  );
};

export default Home;
